import { Validators } from "@angular/forms";
import { debounceTime, distinctUntilChanged, filter, pairwise, startWith, takeUntil } from 'rxjs';
import { AxiosError } from "axios";
import { CompanySearchAutocompleteItem } from "src/app/shared/company-search-service/company-search.service";
import { errorResponse } from "src/types/error-response";
import { InvoiceOcr } from "../../invoice-ocr/invoice-ocr.service";
import { AddressControls, DebtorControls, DebtorForm } from "../form.controls";
import { DEBTOR_FORM_NAME, FormFactory } from "../form.factory";
import { Debtor } from "../form.resources";

export function getDebtorForm(this: FormFactory): DebtorForm {
  const data = this.data as Debtor | undefined;
  const is_editable = data?.is_editable !== false;

  const form = this.fb.group<DebtorControls>({
    id: this.fb.control(data?.id ?? (this.formService.isGuest ? new Date().valueOf().toString() : null)),
    jurisdiction: this.fb.control(data?.jurisdiction ?? 'HU', { validators: Validators.required, nonNullable: true }),
    debtor_type: this.fb.control(data?.debtor_type ?? 'org', { nonNullable: true }),
    party_type_id: this.fb.control(data?.party_type_id ?? null),
    name: this.fb.control(data?.name ?? null, [Validators.required, Validators.maxLength(255)]),
    tax_number: this.fb.control(data?.tax_number ?? null, Validators.required),
    address: this.fb.group<AddressControls>({
      country_iso: this.fb.control(data?.address?.country_iso || 'HU'),
      postcode: this.fb.control(data?.address?.postcode ?? null, [Validators.required, Validators.maxLength(255)]),
      settlement: this.fb.control(data?.address?.settlement ?? null, [Validators.required, Validators.maxLength(255)]),
      street: this.fb.control(data?.address?.street ?? null, [Validators.required, Validators.maxLength(255)]),
    }),
    is_editable: this.fb.control(is_editable, { nonNullable: true }),
  });

  form.controls.jurisdiction.valueChanges
    .pipe(
      startWith(form.controls.jurisdiction.value),
      distinctUntilChanged(),
      pairwise(),
    )
    .subscribe({
      next: async ([prev, current]) => {
        try {
          await this.formService.changeJurisdiction(current);
        } catch (error) {
          console.error('Error while changing jurisdiction', error);
          form.controls.jurisdiction.patchValue(prev, { emitEvent: false });
        }
      }
    });

  form.controls.debtor_type.valueChanges
    .pipe(
      startWith(data?.debtor_type),
      distinctUntilChanged(),
    )
    .subscribe({
      next: (debtor_type) => {
        const { tax_number, address } = form.controls;
        if (debtor_type === 'ind') {
          tax_number.disable({ emitEvent: false });
          address.controls.postcode.setValidators([Validators.required, Validators.maxLength(255)]);
          address.controls.postcode.updateValueAndValidity();
          address.controls.settlement.setValidators([Validators.required, Validators.maxLength(255)]);
          address.controls.settlement.updateValueAndValidity();
          address.controls.street.setValidators([Validators.required, Validators.maxLength(255)]);
          address.controls.street.updateValueAndValidity();
        } else {
          tax_number.enable({ emitEvent: false });
          address.controls.postcode.setValidators([Validators.maxLength(255)]);
          address.controls.postcode.updateValueAndValidity();
          address.controls.settlement.setValidators([Validators.maxLength(255)]);
          address.controls.settlement.updateValueAndValidity();
          address.controls.street.setValidators([Validators.maxLength(255)]);
          address.controls.street.updateValueAndValidity();
        }
      },
    });

  if (is_editable === false) {
    if (form.valid) {
      form.disable({ emitEvent: false, onlySelf: true });
      return form;
    } else {
      form.controls.jurisdiction.disable({ emitEvent: false, onlySelf: true });
    }
  }

  form.valueChanges
    .pipe(
      startWith(form.value),
      pairwise(),
      filter(([prev, current]) => prev.jurisdiction === current.jurisdiction),
      debounceTime(1000),
      takeUntil(this.formService.jurisdictionChange),
      filter(() => form.valid),
    )
    .subscribe({
      next: async () => {
        try {
          await this.formService.upsertDebtor(form.getRawValue());
          this.trackValidForm(DEBTOR_FORM_NAME);
        } catch (error) {
          console.error('Error while saving debtor', error);
          if (error instanceof AxiosError) {
            if (error.response?.status === 422) {
              const errorData = errorResponse.safeParse(error.response?.data);
              if (errorData.success) {
                Object.entries(errorData.data.errors).forEach(([path, errors]) => {
                  const control = form.get(path);
                  control?.markAsDirty();
                  control?.setErrors({
                    unknown: errors[0],
                  }, {
                    emitEvent: false,
                  });
                });
              }
            }
          }

          form.setErrors({
            unknown: true,
          });
        }
      }
    });

  return form;
}

export function hasIdentifiedDebtor(debtor: Debtor | null | undefined): boolean {
  return !!(debtor?.name?.trim() || debtor?.tax_number?.trim());
}

export function isDebtorMatchingInvoiceOcr(
  debtor: Debtor,
  invoice: InvoiceOcr,
): boolean {
  if (!hasIdentifiedDebtor(debtor)) {
    return false;
  }

  return (
    debtor.name === invoice.debtor_name
    || debtor.tax_number?.slice(0, 8) === invoice.debtor_tax_number?.slice(0, 8)
  );
}

export function patchDebtorByInvoiceOcr(
  debtorForm: DebtorForm,
  invoiceData: InvoiceOcr,
  companyData: CompanySearchAutocompleteItem | null,
): void {
  const countryIso = companyData?.country?.iso ?? invoiceData?.debtor_country_iso ?? null;

  debtorForm.patchValue({
    name: companyData?.name ?? invoiceData?.debtor_name,
    tax_number: companyData?.tax_number ?? invoiceData?.debtor_tax_number,
    debtor_type: companyData ? 'org' : (invoiceData?.debtor_type ?? 'org'),
    ...(countryIso ? { jurisdiction: countryIso } : {}),
  });

  debtorForm.patchValue({
    party_type_id: companyData?.party_type?.internal_id,
    address: {
      country_iso: countryIso,
      postcode: companyData?.postcode ?? invoiceData?.debtor_postcode,
      settlement: companyData?.settlement ?? invoiceData?.debtor_city,
      street: companyData?.street ?? invoiceData?.debtor_street,
    },
  });
}
